<template>
    <section :class="['login', 'widget', { 'widget--disabled': !isLoginWidgetEnabled}]">
        <header>
            <h1 class="widget__title">{{ $t('registerMobileNumber.greetings') }}</h1>
        </header>

        <form class="phone-number"
            @submit.prevent
        >
            <phone-number :showInvalidPhoneError="showInvalidPhoneError"
                :is-blocked-country="isBlockedCountry"
                :disabled="isPhoneNumberInputDisabled"
            />

            <button v-if="showSendCodeBySMSButton"
                :class="['widget__button', {
                    'widget__button--disabled': areSendCodeButtonsDisabled
                }]"
                @click="sendLoginCodeBySMS"
            >
                {{ $t('registerMobileNumber.sendBySMS') }}
            </button>
            <button v-else
                id="button-open-captcha"
                ref="openCaptchaButton"
                :class="['widget__button', { 'widget__button--disabled': isOpenCaptchaButtonDisabled}]"
                @click="onOpenCaptchaButtonClicked"
            >
                {{ sendCodeButtonText }}
            </button>

            <button v-if="showSendCodeToEmailButton"
                id="button-send-verification-code-to-email"
                :class="['widget__button widget__secondary-button', {
                    'widget__button--disabled': areSendCodeButtonsDisabled
                }]"
                @click="sendCodeToEmail"
            >
                {{ sendCodeToEmailButtonText }}
            </button>

            <loading-spinner v-if="isLoading" />

            <sms-verification ref="smsVerification"
                :is-login="isLogin"
                :disabled="!isSMSVerificationFieldEnabled"
                @timerStarted="onCodeInputTimerStarted"
                @timerExpired="onCodeInputTimerExpired"
                @blockInput="isSMSVerificationFieldEnabled = false"
            />
        </form>
    </section>
</template>

<script>
// 3rd party
import axios from 'axios';

// Store
import {
    mapGetters,
    mapActions,
} from 'vuex';
import localStoreTypes from '@webapp/store/types.js';
import {
    geetestInit,
} from '@shared/setups/geetest/geetest';

// Helpers
import apiUrl from '@webapp/helpers/api-url.js';

// Components
import loadingSpinner from '@webapp/components/loading-spinner/loading-spinner.vue';
import phoneNumber from '@webapp/components/login-process/phone-number.vue';
import smsVerification from '@webapp/components/login-process/sms-verification.vue';

// Shared Modules
import {
    EventNames,
} from '@shared/utils/analytics-keys';

const FETCH_CODE_METHODS = {
    LOGIN_BY_EMAIL: 'LOGIN_BY_EMAIL',
    LOGIN_BY_SMS: 'LOGIN_BY_SMS',
    REGISTER_BY_SMS: 'REGISTER_BY_SMS',
};

export default {
    name: 'login-widget',
    components: {
        loadingSpinner,
        phoneNumber,
        smsVerification,
    },
    data() {
        return {
            isPhoneNumberInputDisabled: false,
            isLoading: false,
            isLogin: false,
            loginAttempts: 1,
            isCaptchaValidated: false,
            captchaInfo: {},
            isTimerActive: false,
            intervalId: null,
            watchGeetestAvailability: null,
            areSendCodeButtonsDisabled: false,
            showSendCodeBySMSButton: false,
            showSendCodeToEmailButton: false,
            isSMSVerificationFieldEnabled: false,
            showInvalidPhoneError: false,
            isBlockedCountry: false,
            userEmail: '',
            onOpenCaptchaButtonClicked: () => ({}),
            captchaTimeoutInfo: {},
            timeout: null,
        };
    },
    computed: {
        ...mapGetters([
            'isLoginWidgetEnabled',
            'registrationDetails',
            'getLatestError',
            'getLatestNonProcessableError',
            'isGeetestInitialized',
        ]),

        ...mapGetters('countries', ['countries']),

        ...mapGetters('auth', ['getRequestCodeResponse']),

        ...mapGetters('remoteConfig', ['remoteConfigByKey']),

        isGeetestDisabled() {
            return this.remoteConfigByKey('isGeestestDisabled');
        },

        sendCodeButtonText() {
            return this.loginAttempts <= 1 ? this.$t('registerMobileNumber.sendCode') : this.$t('registerCode.requestNewCode');
        },

        sendCodeToEmailButtonText() {
            return this.$t('registerMobileNumber.sendCodeToEmail', {
                email: this.userEmail,
            });
        },

        isOpenCaptchaButtonDisabled() {
            return !(this.selectedPhoneCode && this.registrationDetails.phoneNumber) ||
                this.isLoading ||
                this.isTimerActive ||
                this.showInvalidPhoneError ||
                this.isBlockedCountry;
        },

        selectedPhoneCode() {
            const {
                countryCode = '',
            } = this.registrationDetails;

            return this.countries.find(({ iso2 }) => iso2 === countryCode)?.internationalCallingCode ?? '';
        },

        fetchCodePayload() {
            return {
                countryCode: this.registrationDetails.countryCode,
                mobileNumber: this.registrationDetails.phoneNumber,
                captchaInformation: {
                    lotNumber: this.captchaInfo.lot_number,
                    captchaOutput: this.captchaInfo.captcha_output,
                    passToken: this.captchaInfo.pass_token,
                    genTime: this.captchaInfo.gen_time,
                    captchaId: this.captchaInfo.captcha_id,
                    signToken: this.captchaInfo.sign_token,
                    version: 'v4',
                    appType: 'webapp',
                },
            };
        },
    },
    watch: {
        getRequestCodeResponse: 'onCodeSent',

        selectedPhoneCode: 'onPhoneChange',
        'registrationDetails.phoneNumber': 'onPhoneChange',

        getLatestError: 'onError',
        getLatestNonProcessableError: 'onNonProcessableError',
    },
    mounted() {
        this.watchGeetestAvailability = this.$watch('isGeetestDisabled', (value) => {
            if (!value && !this.isGeetestInitialized) {
                geetestInit(
                    {
                        product: 'bind',
                    },
                    {
                        preValidationHandlerCallback: this.checkSubmit,
                        readyCallback: () => this.$logger.info('captcha ready'),
                        successCallback: this.success,
                        errorCallback: (e) => this.$logger.error('Error Captcha.', e),
                    }
                );

                this.setGeetestInitialized(true);

                return;
            }

            this.setSuccessFunctionOnCaptchaButton();
        }, {
            immediate: true,
        });
    },
    beforeDestroy() {
        clearInterval(this.intervalId);
        this.watchGeetestAvailability();
    },
    methods: {
        ...mapActions({
            setRegistration: localStoreTypes.SET_REGISTRATION,
            resetModal: localStoreTypes.RESET_MODAL,
            setModal: localStoreTypes.SET_MODAL,
            setGeetestInitialized: localStoreTypes.SET_GEETEST_INITIALIZED,
        }),

        ...mapActions('auth', [
            'fetchCodeByEmail',
            'fetchCodeBySMS',
            'fetchCodeForRegistration',
        ]),

        checkSubmit(captchaObj) {
            this.onOpenCaptchaButtonClicked = () => {
                this.$webTrackingInstance?.sendEvent(EventNames.smsCodeEntryClick);
                this.setCloseCaptchaTimeout();
                this.captchaTimeoutInfo = captchaObj;
                captchaObj.showCaptcha();
            };
        },

        success(res) {
            this.captchaInfo = res;
            this.validatePhoneNumber();
        },

        onCodeSent() {
            this.isLoading = false;
            this.loginAttempts++;
            this.$refs.smsVerification.startCountdown();
            this.isSMSVerificationFieldEnabled = true;
        },

        onPhoneChange() {
            this.showInvalidPhoneError = false;
            this.showSendCodeBySMSButton = false;
            this.showSendCodeToEmailButton = false;
            this.isSMSVerificationFieldEnabled = false;
            this.captchaInfo = null;
        },

        onCodeInputTimerStarted() {
            this.isTimerActive = true;
            this.areSendCodeButtonsDisabled = true;
            this.isSMSVerificationFieldEnabled = true;
        },

        onCodeInputTimerExpired() {
            this.isTimerActive = false;
            this.showSendCodeBySMSButton = false;
            this.showSendCodeToEmailButton = false;
        },

        async fetchMobileNumberValidation() {
            const result = await axios.post(`${apiUrl(3)}/user/validation/mobileNumber`, {
                countryCode: this.registrationDetails.countryCode || '',
                mobileNumber: this.registrationDetails.phoneNumber,
            });

            return result.data.data;
        },

        async validatePhoneNumber() {
            if (this.isOpenCaptchaButtonDisabled) {
                return;
            }

            this.isLoading = true;

            try {
                const {
                    canRegister, isValid, isAvailable, email, formattedNumber,
                } = await this.fetchMobileNumberValidation();

                if (!this.canProceedWithCodeSending(canRegister, isValid)) {
                    return;
                }

                this.setRegistration([
                    {
                        property: 'formattedPhoneNumber',
                        value: formattedNumber,
                    },
                    {
                        property: 'internationalCallingCode',
                        value: this.selectedPhoneCode,
                    },
                ]);

                this.isLogin = !isAvailable;
                if (this.isLogin) {
                    this.$webTrackingInstance?.sendEvent(EventNames.LoginStart);
                    if (email) {
                        this.onUserHasEmailOptionAvailable(email);
                    } else {
                        await this.sendLoginCodeBySMS();
                    }
                } else {
                    this.$webTrackingInstance?.sendEvent(EventNames.SignUpStart);
                    await this.sendCode(FETCH_CODE_METHODS.REGISTER_BY_SMS);
                }
            } catch (error) {
                const fetchErrors = this.utilGlobalHandleErrorMessages(error);

                this.loginAttempts++;
                this.$notify({
                    type: 'error',
                    text: fetchErrors.message,
                });
            } finally {
                this.isLoading = false;
            }
        },

        canProceedWithCodeSending(canRegister, isValid) {
            if (canRegister === false) {
                this.isBlockedCountry = true;

                return false;
            } else if (!isValid) {
                this.showInvalidPhoneError = true;

                return false;
            }

            return true;
        },

        onUserHasEmailOptionAvailable(email) {
            this.userEmail = email;
            this.showSendCodeBySMSButton = true;
            this.showSendCodeToEmailButton = true;
            this.areSendCodeButtonsDisabled = false;
            this.isSMSVerificationFieldEnabled = false;
        },

        async sendLoginCodeBySMS() {
            await this.sendCode(FETCH_CODE_METHODS.LOGIN_BY_SMS);
        },

        async sendCodeToEmail() {
            await this.sendCode(FETCH_CODE_METHODS.LOGIN_BY_EMAIL);
        },

        async sendCode(method) {
            this.isLoading = true;
            this.areSendCodeButtonsDisabled = true;
            this.isPhoneNumberInputDisabled = true;

            switch (method) {
                case FETCH_CODE_METHODS.LOGIN_BY_SMS:
                    await this.fetchCodeBySMS(this.fetchCodePayload);
                    break;
                case FETCH_CODE_METHODS.LOGIN_BY_EMAIL:
                    await this.fetchCodeByEmail(this.fetchCodePayload);
                    break;
                case FETCH_CODE_METHODS.REGISTER_BY_SMS:
                    await this.fetchCodeForRegistration(this.fetchCodePayload);
                    break;
                default:
                    break;
            }

            this.isPhoneNumberInputDisabled = false;
            this.isLoading = false;
        },

        onError(error) {
            const fetchErrors = this.utilGlobalHandleErrorMessages(error);

            if (!fetchErrors.message) {
                return;
            }

            this.$notify({
                type: 'error',
                text: fetchErrors.message,
            });

            this.isLoading = false;
            this.loginAttempts++;
            this.areSendCodeButtonsDisabled = true;
        },

        onNonProcessableError() {
            this.setSuccessFunctionOnCaptchaButton();
        },

        onCaptchaValidated(value) {
            if (!value.success) {
                return;
            }
            this.isCaptchaValidated = value.success;
            this.captchaInfo = value.captchaInfo;
        },

        setSuccessFunctionOnCaptchaButton() {
            this.onOpenCaptchaButtonClicked = () => {
                this.success({
                    'lot_number': 'bypass-captcha',
                    'captcha_output': 'bypass-captcha',
                    'pass_token': 'bypass-captcha',
                    'gen_time': 'bypass-captcha',
                });
            };
        },

        setCloseCaptchaTimeout() {
            this.timeout = setTimeout(this.hideCaptcha, 60000);
        },

        hideCaptcha() {
            this.captchaTimeoutInfo.destroy();
            clearTimeout(this.timeout);
        },

    },
};
</script>

<style lang="scss" scoped>
.widget {
    width: 100%;
    box-sizing: border-box;
    align-self: start;
    background-color: $white-color;

    .sms-verification {
        margin-top: 2rem;
    }

    &__secondary-button {
        margin-top: 2rem;
        padding: 0.5rem;
        color: $daintree-color;
        display: block;
        background: none;
        margin-bottom: 0;
        border: 0;
    }
}
</style>